import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { betriebstageBuchService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import SignatureCanvas from "react-signature-canvas";
import moment from "moment";
import { useTheme } from "../context/ThemeContext";
import { Form, Button, Card, Row, Col, Alert, Spinner, Nav, Tab, Badge } from "react-bootstrap";

// ...removed unused TabPanel helper...

const BetriebstageBuchForm = () => {
  // Weather code mapping for Open-Meteo API (German descriptions)
  const getWeatherDescription = (code) => {
    const weatherCodes = {
      0: 'Klarer Himmel',
      1: 'Überwiegend klar',
      2: 'Teilweise bewölkt',
      3: 'Bedeckt',
      45: 'Nebel',
      48: 'Abscheidender Raureifnebel',
      51: 'Leichter Nieselregen',
      53: 'Mäßiger Nieselregen',
      55: 'Dichter Nieselregen',
      56: 'Leichter gefrierender Nieselregen',
      57: 'Dichter gefrierender Nieselregen',
      61: 'Leichter Regen',
      63: 'Mäßiger Regen',
      65: 'Starker Regen',
      66: 'Leichter gefrierender Regen',
      67: 'Starker gefrierender Regen',
      71: 'Leichter Schneefall',
      73: 'Mäßiger Schneefall',
      75: 'Starker Schneefall',
      77: 'Schneekörner',
      80: 'Leichte Regenschauer',
      81: 'Mäßige Regenschauer',
      82: 'Heftige Regenschauer',
      85: 'Leichte Schneeschauer',
      86: 'Starke Schneeschauer',
      95: 'Gewitter',
      96: 'Gewitter mit leichtem Hagel',
      99: 'Gewitter mit schwerem Hagel'
    };
    return weatherCodes[code] || `Wettercode ${code}`;
  };

  // Fetch weather and temperature using Open-Meteo API
  const fetchWeather = async (lat = 52.52, lon = 13.405) => {
    try {
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`
      );
      const data = await response.json();
      if (data.current_weather) {
        setFormData(prev => ({
          ...prev,
          temperature: data.current_weather.temperature?.toString() || "",
          weather: typeof data.current_weather.weathercode !== "undefined" ? getWeatherDescription(data.current_weather.weathercode) : ""
        }));
      }
    } catch (err) {
      // Optionally set error or fallback
    }
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isManager } = useContext(AuthContext);
  const { t } = useLanguage();
  const { mode } = useTheme();
  const signatureRef = useRef(null);

  // Default German conveyor types for the recycling center
  const defaultConveyorTypes = [
    "AT",
    "Steigband",
    "Trommel",
    "HeckBand",
    "SortierBand",
    "MagnetSortierHaus",
    "MagnetHalle",
    "SeitenBand",
    "Zuführband",
    "TrommelWindsichter",
    "SpeedBand",
    "Airlift",
    "Airwheel",
    "Ventilator",
  ];

  const [formData, setFormData] = useState({
    date: moment().format("YYYY-MM-DD"),
    conveyorChecks: [],
    specialChecks: [],
    generalNotes: "",
    creatorSignature: null,
    shiftStart: "07:00",
    shiftEnd: "16:00",
    weather: "",
    temperature: "",
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // Initialize with default conveyor checks if this is a new entry
  useEffect(() => {
    setLoading(true);
    // Check if we're in edit mode (id exists)
    if (id) {
      setIsEditMode(true);
      fetchEntry();
    } else {
      // Initialize with default conveyor types and special checks
      setFormData(prev => ({
        ...prev,
        conveyorChecks: defaultConveyorTypes.map((name) => ({
          name,
          functional: true,
          notes: "",
        })),
        specialChecks: [
          {
            name: "Eigen-,Fremd, und Anlieferkontrolle",
            completed: true,
            notes: "Keine Vorkommnisse",
          },
          {
            name: "Reinigung",
            completed: true,
            notes: "",
          },
          {

            name: "Betriebsstunden Doppstadtanlage",
            completed: true,
            notes: "",

          },
          {
            name: "Container Abschmierung Rollen Hofabrollcontainer",
            completed: true,
            notes: "",
          },
        ],
      }));
      // Try to use browser geolocation, fallback to Berlin
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
          () => fetchWeather() // fallback
        );
      } else {
        fetchWeather();
      }
      setLoading(false);
    }

    // Use the current user's signature if available
    if (currentUser && currentUser.signature) {
      setFormData((prev) => ({
        ...prev,
        creatorSignature: currentUser.signature,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentUser]);

  // Fetch the entry if in edit mode
  const fetchEntry = async () => {
    try {
      setFetchLoading(true);
      const response = await betriebstageBuchService.getById(id);
      const entry = response.data;

      // Format date for input field
      const formattedDate = moment(entry.date).format("YYYY-MM-DD");

      setFormData({
        date: formattedDate,
        conveyorChecks: entry.conveyorChecks || [],
        specialChecks: entry.specialChecks || [],
        generalNotes: entry.generalNotes || "",
        creatorSignature: entry.creatorSignature || null,
        shiftStart: entry.shiftStart || "",
        shiftEnd: entry.shiftEnd || "",
        weather: entry.weather || "",
        temperature: entry.temperature?.toString() || "",
      });
    } catch (err) {
      console.error("Error fetching BetriebstageBuch entry:", err);
      setError(t("error") + ": " + t("noDataAvailable"));
    } finally {
      setFetchLoading(false);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChecklistChange = (listName, index, field, value) => {
    setFormData((prev) => {
      const newList = [...prev[listName]];
      newList[index] = { ...newList[index], [field]: value };
      return { ...prev, [listName]: newList };
    });
  };

  const addChecklistItem = (listName, defaultItem) => {
    setFormData((prev) => ({ ...prev, [listName]: [...prev[listName], defaultItem] }));
  };

  const removeChecklistItem = (listName, index) => {
    setFormData((prev) => ({ ...prev, [listName]: prev[listName].filter((_, i) => i !== index) }));
  };

  const handleSignatureConfirm = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setFormData((prev) => ({ ...prev, creatorSignature: signatureRef.current.getTrimmedCanvas().toDataURL("image/png") }));
    }
  };
  const clearSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
    setFormData((prev) => ({ ...prev, creatorSignature: null }));
  };
  const useProfileSignature = () => {
    if (currentUser?.signature) {
      setFormData((prev) => ({ ...prev, creatorSignature: currentUser.signature }));
      if (signatureRef.current) signatureRef.current.clear(); // Clear canvas if profile sig is used
    } else {
      setError(t('noProfileSignatureAvailable'));
    }
  };


  const validateForm = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = t("dateIsRequired");
    if (!formData.creatorSignature) newErrors.creatorSignature = t("signatureIsRequired");
    // Add more specific validations as needed, e.g., for conveyor names if they are editable
    formData.conveyorChecks.forEach((cc, index) => {
      if (!cc.name || cc.name.trim() === "") {
        newErrors[`conveyorName_${index}`] = t('conveyorNameRequired');
      }
    });
    formData.specialChecks.forEach((sc, index) => {
      if (!sc.name || sc.name.trim() === "") {
        newErrors[`specialCheckName_${index}`] = t('checkNameRequired');
      }
    });
    // setValidationErrors(newErrors); // Remove, not used in Bootstrap version
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isManager()) { setError(t("managersOnlyCanCreateBTB")); return; }
    if (!validateForm()) { setError(t('pleaseCorrectFormErrors')); return; }

    setLoading(true); setError(null); setSuccess(false);
    try {
      const dataToSubmit = { ...formData, creator: currentUser._id, creatorName: currentUser.name };
      let response;
      if (isEditMode) {
        response = await betriebstageBuchService.update(id, dataToSubmit);
      } else {
        response = await betriebstageBuchService.create(dataToSubmit);
      }
      setSuccess(true);
      setTimeout(() => navigate(`/betriebstagebuch/${response.data.entry._id}`), 1500);
    } catch (err) {
      console.error("Error saving entry:", err);
      setError(err.response?.data?.message || err.message || t("errorSavingEntry"));
    } finally {
      setLoading(false);
    }
  };

  const calculateSectionProgress = (sectionName) => {
    const section = formData[sectionName];
    if (!section || section.length === 0) return sectionName === 'specialChecks' ? 100 : 0; // Special checks can be empty and "complete"
    const total = section.length;
    let completed = 0;
    if (sectionName === 'conveyorChecks') {
      completed = section.filter(item => item.name?.trim() && item.functional).length;
    } else if (sectionName === 'specialChecks') {
      completed = section.filter(item => item.name?.trim() && item.completed).length;
    }
    return Math.round((completed / total) * 100);
  };

  const formTabsMeta = [
    { key: "general", label: t("general"), icon: <i className="bi bi-info-circle"></i>, progress: calculateSectionProgress('general') },
    { key: "conveyorChecks", label: t("conveyorChecks"), icon: <i className="bi bi-gear-wide-connected"></i>, list: 'conveyorChecks', progress: calculateSectionProgress('conveyorChecks') },
    { key: "specialChecks", label: t("specialChecks"), icon: <i className="bi bi-check-circle"></i>, list: 'specialChecks', progress: calculateSectionProgress('specialChecks') },
    { key: "generalNotes", label: t("generalNotes"), icon: <i className="bi bi-journal-text"></i>, fields: ['generalNotes'] },
    { key: "signature", label: t("signature"), icon: <i className="bi bi-pencil-square"></i>, fields: ['creatorSignature'] }
  ];

  if (fetchLoading) {
    return <div className="text-center my-5"><Spinner animation="border" role="status"><span className="visually-hidden">{t("loading")}</span></Spinner><p className="mt-2">{t("loading")}...</p></div>;
  }
  if (!isManager()) {
    return <Alert variant="danger" className="my-5">{t("managersOnlyCanCreateBTB")}</Alert>;
  }

  return (
    <div className={mode === "dark" ? "bg-dark text-light" : ""}>
      <Card as="form" onSubmit={handleSubmit} className={`shadow-sm p-4 ${mode === "dark" ? "bg-dark text-light border-secondary" : ""}`}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h5 font-weight-bold mb-0">{isEditMode ? t("editBetriebstagebuch") : t("createBetriebstagebuch")}</h1>
          <div>
            <Button variant="outline-secondary" onClick={() => navigate(isEditMode ? `/betriebstagebuch/${id}` : "/betriebstagebuch")} disabled={loading} className="me-2">
              <i className="bi bi-arrow-left"></i> {t("cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={loading || success}>
              {loading ? <Spinner animation="border" size="sm" /> : success ? t("saved") : (isEditMode ? t("updateEntry") : t("saveEntry"))}
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
        {success && <Alert variant="success" className="mb-4">{isEditMode ? t("entryUpdatedSuccess") : t("entryCreatedSuccess")}</Alert>}

        <Row>
          <Col md={3} className={`border-end pe-4 ${mode === "dark" ? "bg-dark text-light border-secondary" : ""}`}>
            <Nav variant="pills" className={`flex-column mb-4 ${mode === "dark" ? "bg-dark text-light" : ""}`}>
              {formTabsMeta.map((tab, index) => (
                <Nav.Item key={tab.key} className="mb-2">
                  <Nav.Link
                    active={activeTab === tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="d-flex align-items-center"
                  >
                    {tab.icon}
                    <span className="ms-2">{tab.label}</span>
                    {tab.progress !== undefined && (
                      <Badge bg={mode === "dark" ? "light" : "primary"} text={mode === "dark" ? "dark" : "light"} className="ms-auto">
                        {tab.progress}%
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Col>

          <Col md={9} className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}>
            <Tab.Content>
              {/* General Info */}
              <Tab.Pane active={activeTab === "general"}>
                <h5 className="mb-4">{t("generalInformation")}</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("entryDate")}</Form.Label>
                      <Form.Control
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("creator")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={currentUser?.name || ""}
                        disabled
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("shiftStart")}</Form.Label>
                      <Form.Control
                        type="time"
                        name="shiftStart"
                        value={formData.shiftStart}
                        onChange={handleChange}
                        step="60"
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("shiftEnd")}</Form.Label>
                      <Form.Control
                        type="time"
                        name="shiftEnd"
                        value={formData.shiftEnd}
                        onChange={handleChange}
                        step="60"
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("weather")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="weather"
                        value={formData.weather}
                        onChange={handleChange}
                        placeholder={t("weatherPlaceholder")}
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className={mode === "dark" ? "text-light" : ""}>{t("temperature")}</Form.Label>
                      <Form.Control
                        type="number"
                        name="temperature"
                        value={formData.temperature}
                        onChange={handleChange}
                        placeholder={t("temperaturePlaceholder")}
                        className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                        style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-4">
                  <Button variant="primary" onClick={() => setActiveTab("conveyors")}>
                    {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                  </Button>
                </div>
              </Tab.Pane>

              {/* Conveyor Checks */}
              <Tab.Pane active={activeTab === "conveyorChecks"}>
                <h5 className="mb-4">{t("conveyorChecks")}</h5>
                <Button
                  variant="outline-primary"
                  className="mb-3"
                  onClick={() => addChecklistItem('conveyorChecks', { name: "", functional: true, notes: "" })}
                  disabled={loading}
                >
                  <i className="bi bi-plus-circle"></i> {t("addConveyor")}
                </Button>
                {formData.conveyorChecks.map((check, index) => (
                  <Card key={index} className={`mb-3 ${mode === "dark" ? "bg-dark text-light border-secondary" : ""}`}>
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>{t("conveyorName")}</Form.Label>
                            <Form.Control
                              type="text"
                              value={check.name}
                              onChange={e => handleChecklistChange('conveyorChecks', index, 'name', e.target.value)}
                              size="sm"
                              disabled={loading}
                              className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                              style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs="auto">
                          <Form.Group className="mb-2">
                            <Form.Check
                              type="switch"
                              id={`functional-switch-${index}`}
                              label={check.functional ? t("functional") : t("notFunctional")}
                              checked={check.functional}
                              onChange={e => handleChecklistChange('conveyorChecks', index, 'functional', e.target.checked)}
                              disabled={loading}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>{t("notes")}</Form.Label>
                            <Form.Control
                              as="textarea"
                              value={check.notes}
                              onChange={e => handleChecklistChange('conveyorChecks', index, 'notes', e.target.value)}
                              size="sm"
                              rows={2}
                              disabled={loading}
                              className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                              style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs="auto">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeChecklistItem('conveyorChecks', index)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </Tab.Pane>

              {/* Special Checks */}
              <Tab.Pane active={activeTab === "specialChecks"}>
                <h5 className="mb-4">{t("specialChecks")}</h5>
                <Button
                  variant="outline-primary"
                  className="mb-3"
                  onClick={() => addChecklistItem('specialChecks', { name: "", completed: true, notes: "" })}
                  disabled={loading}
                >
                  <i className="bi bi-plus-circle"></i> {t("addSpecialCheck")}
                </Button>
                {formData.specialChecks.map((check, index) => (
                  <Card key={index} className={`mb-3 ${mode === "dark" ? "bg-dark text-light border-secondary" : ""}`}>
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>{t("checkName")}</Form.Label>
                            <Form.Control
                              type="text"
                              value={check.name}
                              onChange={e => handleChecklistChange('specialChecks', index, 'name', e.target.value)}
                              size="sm"
                              disabled={loading}
                              className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                              style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs="auto">
                          <Form.Group className="mb-2">
                            <Form.Check
                              type="switch"
                              id={`completed-switch-${index}`}
                              label={check.completed ? t("completed") : t("notCompleted")}
                              checked={check.completed}
                              onChange={e => handleChecklistChange('specialChecks', index, 'completed', e.target.checked)}
                              disabled={loading}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>{t("notes")}</Form.Label>
                            <Form.Control
                              as="textarea"
                              value={check.notes}
                              onChange={e => handleChecklistChange('specialChecks', index, 'notes', e.target.value)}
                              size="sm"
                              rows={2}
                              disabled={loading}
                              className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                              style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs="auto">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeChecklistItem('specialChecks', index)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </Tab.Pane>

              {/* General Notes */}
              <Tab.Pane active={activeTab === "generalNotes"}>
                <h5 className="mb-4">{t("generalNotes")}</h5>
                <Form.Group>
                  <Form.Label>{t("notesAndObservations")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="generalNotes"
                    value={formData.generalNotes}
                    onChange={handleChange}
                    rows={6}
                    disabled={loading}
                    className={mode === "dark" ? "bg-dark text-light border-secondary" : ""}
                    style={mode === "dark" ? { backgroundColor: "#212529", color: "#fff", borderColor: "#6c757d" } : {}}
                  />
                </Form.Group>
              </Tab.Pane>

              {/* Signature */}
              <Tab.Pane active={activeTab === "signature"}>
                <h5 className="mb-4">{t("creatorSignature")}*</h5>
                {formData.creatorSignature ? (
                  <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '8px', textAlign: 'center', marginBottom: '16px' }}>
                    <img src={formData.creatorSignature} alt={t("creatorSignature")} style={{ maxWidth: '100%', maxHeight: 150, display: 'block', margin: 'auto' }} />
                    <Button onClick={() => { setFormData(prev => ({ ...prev, creatorSignature: null })); if (signatureRef.current) signatureRef.current.clear(); }} size="sm" style={{ marginTop: '8px' }} disabled={loading}>{t("clearAndRedraw")}</Button>
                  </div>
                ) : (
                  <Card className={`mb-4 ${mode === "dark" ? "bg-dark text-light border-secondary" : ""}`}>
                    <Card.Body>
                      <SignatureCanvas
                        ref={signatureRef}
                        penColor={mode === "dark" ? "#fff" : "#000"}
                        canvasProps={{ style: { width: '100%', height: 200 } }}
                        onEnd={() => handleSignatureConfirm()}
                        disabled={loading}
                      />
                      <div className="d-flex justify-content-end mt-2">
                        <Button variant="secondary" size="sm" onClick={clearSignature} disabled={loading}>
                          <i className="bi bi-eraser"></i> {t("clear")}
                        </Button>
                        {currentUser?.signature && (
                          <Button variant="outline-primary" size="sm" onClick={useProfileSignature} className="ms-2" disabled={loading}>
                            <i className="bi bi-person-fill"></i> {t("useProfileSignature")}
                          </Button>
                        )}
                        {!formData.creatorSignature && (
                          <Button variant="primary" size="sm" onClick={handleSignatureConfirm} className="ms-2" disabled={loading}>
                            {t("confirmSignature")}
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Tab.Pane>
            </Tab.Content>

            {/* Navigation buttons within content area */}
            <div className="d-flex justify-content-between align-items-center mt-4">
              {activeTab !== "general" && (
                <Button variant="outline-secondary" onClick={() => setActiveTab("general")} disabled={loading}>
                  <i className="bi bi-arrow-left"></i> {t("back")}
                </Button>
              )}
              {activeTab !== "signature" && (
                <Button variant="primary" onClick={() => setActiveTab("signature")} disabled={loading}>
                  {t("finish")} <i className="bi bi-check2-circle ms-1"></i>
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default BetriebstageBuchForm;
